// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/Condicional.tsx
//  Consulta de Vendas Condicionais. Busca, status, período, cliente, vendedor,
//  ordenação e KPIs rodam NO SERVIDOR; os filtros ficam na URL.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Trash2, Package, Clock, CheckCircle2,
  Eye, Pencil, RotateCcw, AlertCircle, CalendarX, Loader2,
} from 'lucide-react';
import { useCondicional } from '@/features/operacional/hooks/useCondicional';
import type { StatusCondicional } from '@/features/operacional/types';
import { DeleteConfirmDialog } from '@/features/financeiro/components/DeleteConfirmDialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { BarraFiltros } from '@/components/BarraFiltros';
import { BotaoExportar } from '@/components/BotaoExportar';
import { FiltroPeriodo } from '@/components/FiltroPeriodo';
import { PaginacaoRodape } from '@/components/PaginacaoRodape';
import { SortableTableHead } from '@/components/SortableTableHead';
import { PessoaLookupCombobox } from '@/components/PessoaLookupCombobox';
import { VendedorSearchCombobox } from '@/components/VendedorSearchCombobox';
import { useFiltrosNaUrl } from '@/hooks/useFiltrosNaUrl';
import { usePaginacaoServidor } from '@/hooks/usePaginacaoServidor';
import { alternarOrdenacao, type DirecaoOrdenacao } from '@/lib/consultas';

const statusConfig: Record<StatusCondicional, { label: string; className: string; icon: typeof Clock }> = {
  aberta:   { label: 'Aberta',    className: 'bg-blue-500/10 text-blue-500',    icon: Clock        },
  vendida:  { label: 'Vendida',   className: 'bg-green-500/10 text-green-500',  icon: CheckCircle2 },
  devolvida:{ label: 'Devolvida', className: 'bg-gray-500/10 text-gray-500',    icon: RotateCcw    },
  vencida:  { label: 'Vencida',   className: 'bg-orange-500/10 text-orange-500',icon: AlertCircle  },
  expirada: { label: 'Expirada',  className: 'bg-slate-500/10 text-slate-500',  icon: CalendarX    },
};

/** "yyyy-MM-dd" de hoje no relógio local. */
const hojeIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Mesma regra do servidor: vencida = validade ANTERIOR a hoje (o último dia ainda vale). */
function statusDaLinha(isBaixada: boolean, isDevolvido: boolean, dtValidade: string, isExpirada: boolean): StatusCondicional {
  if (isExpirada) return 'expirada';
  if (isDevolvido) return 'devolvida';
  if (isBaixada) return 'vendida';
  if ((dtValidade ?? '').slice(0, 10) < hojeIso()) return 'vencida';
  return 'aberta';
}

/** Data "yyyy-MM-dd…" (coluna date) sem deslocar o dia pelo fuso. */
const dataBr = (iso?: string | null) => {
  if (!iso) return '—';
  const [a, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${a}`;
};

const BRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

type Campo = 'numero' | 'cliente' | 'emissao' | 'validade' | 'valor';

const PADROES = {
  busca: '',
  status: 'todos',
  dtInicio: '',
  dtFim: '',
  clienteId: 0,
  vendedorId: 0,
  ordenarPor: 'emissao',
  direcao: 'desc',
  pagina: 1,
  tamanhoPagina: 20,
};

const Condicional = () => {
  const navigate = useNavigate();
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const { filtros, setFiltros, limparFiltros, filtrosAtivos } = useFiltrosNaUrl(PADROES);
  const pag = usePaginacaoServidor({
    controlado: { pagina: filtros.pagina, tamanhoPagina: filtros.tamanhoPagina, onChange: setFiltros },
  });

  const { condicionais, total, totalPaginas, vencidas, isLoading, isFetching, resumo,
          deleteCondicional, expirarVencidas, isExpirando } = useCondicional({
    pagina: pag.pagina,
    tamanhoPagina: pag.tamanhoPagina,
    filtros: {
      busca: filtros.busca, status: filtros.status, dtInicio: filtros.dtInicio, dtFim: filtros.dtFim,
      clienteId: filtros.clienteId, vendedorId: filtros.vendedorId,
      ordenarPor: filtros.ordenarPor, direcao: filtros.direcao as DirecaoOrdenacao,
    },
  });

  const ordenacao = { ordenarPor: filtros.ordenarPor, direcao: filtros.direcao as DirecaoOrdenacao };
  const onOrdenar = (campo: Campo) =>
    setFiltros(alternarOrdenacao(ordenacao, campo, campo === 'cliente' ? 'asc' : 'desc'));
  const cabecalho = { ...ordenacao, onOrdenar };
  const kpi = (v?: number) => (resumo ? v ?? 0 : '…');

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Venda Condicional</h1>
          <p className="text-muted-foreground mt-1">Gerencie as vendas condicionais (produtos em consignação)</p>
        </div>
        <Button className="gap-2" onClick={() => navigate('/operacional/condicional/novo')}>
          <Plus className="h-4 w-4" />Nova Condicional
        </Button>
      </div>

      {/* Alerta de condicionais vencidas — saldo preso no estoque (achado EST-05) */}
      {vencidas.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-lg border border-orange-500/30 bg-orange-500/10 p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-orange-500 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium text-foreground">
                {vencidas.length} condicional(is) vencida(s) com saldo retido no estoque
              </p>
              <p className="text-sm text-muted-foreground">
                A validade passou e a mercadoria ainda consta em poder do cliente, reduzindo o disponível
                para venda. Faça o acerto/devolução manual ou expire as vencidas (devolve o saldo ao depósito).
              </p>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="ghost" onClick={() => setFiltros({ status: 'vencida' })}>
              Ver vencidas
            </Button>
            <Button
              variant="outline"
              className="gap-2 border-orange-500/40"
              disabled={isExpirando}
              onClick={() => expirarVencidas()}
            >
              <CalendarX className="h-4 w-4" />
              {isExpirando ? 'Expirando...' : 'Expirar vencidas'}
            </Button>
          </div>
        </div>
      )}

      {/* KPIs — calculados no servidor sobre o filtro (todas as páginas) */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Clock className="h-4 w-4" />Abertas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-500">{kpi(resumo?.abertas)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {BRL(resumo?.valorAbertas ?? 0)}
              {(resumo?.vencidas ?? 0) > 0 && ` · ${resumo?.vencidas} vencida(s)`}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />Vendidas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{kpi(resumo?.vendidas)}</div>
            <p className="text-xs text-muted-foreground mt-1">Confirmadas</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <RotateCcw className="h-4 w-4" />Devolvidas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-500">{kpi(resumo?.devolvidas)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Retornadas ao estoque
              {(resumo?.expiradas ?? 0) > 0 && ` · ${resumo?.expiradas} expirada(s)`}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Package className="h-4 w-4" />Total
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{kpi(resumo?.total)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {filtrosAtivos > 0 ? 'condicionais no filtro' : 'condicionais cadastradas'} · {BRL(resumo?.valorTotal ?? 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      <BarraFiltros
        busca={filtros.busca}
        onBuscar={(busca) => setFiltros({ busca })}
        placeholderBusca="Nº da condicional, cliente ou CPF/CNPJ..."
        filtrosAtivos={filtrosAtivos}
        onLimpar={limparFiltros}
        acoes={<BotaoExportar endpoint="/api/condicionais/exportar" params={filtros} />}
      >
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Status</Label>
          <Select value={filtros.status} onValueChange={(status) => setFiltros({ status })}>
            <SelectTrigger className="h-9 w-[150px]" aria-label="Status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              <SelectItem value="aberta">Aberta</SelectItem>
              <SelectItem value="vendida">Vendida</SelectItem>
              <SelectItem value="devolvida">Devolvida</SelectItem>
              <SelectItem value="vencida">Vencida</SelectItem>
              <SelectItem value="expirada">Expirada</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <FiltroPeriodo
          rotulo="Emissão"
          inicio={filtros.dtInicio}
          fim={filtros.dtFim}
          onChange={({ inicio, fim }) => setFiltros({ dtInicio: inicio, dtFim: fim })}
        />
        <div className="space-y-1 w-64">
          <Label className="text-xs text-muted-foreground">Cliente</Label>
          <PessoaLookupCombobox
            papel="cliente"
            value={filtros.clienteId || null}
            onChange={(id) => setFiltros({ clienteId: id ?? 0 })}
            placeholder="Todos os clientes"
            className="h-9"
          />
        </div>
        <div className="space-y-1 w-56">
          <Label className="text-xs text-muted-foreground">Vendedor</Label>
          <VendedorSearchCombobox
            value={filtros.vendedorId || null}
            onChange={(id) => setFiltros({ vendedorId: id ?? 0 })}
            placeholder="Todos os vendedores"
          />
        </div>
        {isFetching && !isLoading && (
          <Loader2 className="h-4 w-4 mb-2.5 animate-spin text-muted-foreground" aria-label="Atualizando" />
        )}
      </BarraFiltros>

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead campo="numero" {...cabecalho} className="w-24">Nº</SortableTableHead>
                  <SortableTableHead campo="cliente" {...cabecalho}>Cliente</SortableTableHead>
                  <TableHead>Vendedor</TableHead>
                  <SortableTableHead campo="emissao" {...cabecalho}>Emissão</SortableTableHead>
                  <SortableTableHead campo="validade" {...cabecalho}>Validade</SortableTableHead>
                  <SortableTableHead campo="valor" {...cabecalho} alinharDireita>Total</SortableTableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
                ) : condicionais.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">{filtros.dtInicio || filtros.dtFim ? 'Nenhuma condicional neste período — amplie o período para ver as anteriores' : 'Nenhuma condicional encontrada'}</TableCell></TableRow>
                ) : (
                  condicionais.map((c) => {
                    const status = statusDaLinha(c.isBaixada, c.isDevolvido, c.dtValidade, c.isExpirada);
                    const cfg    = statusConfig[status];
                    const Icon   = cfg.icon;
                    const podeCriar = !c.isBaixada && !c.isDevolvido;
                    return (
                      <TableRow key={c.id}>
                        <TableCell className="font-medium">{c.numeroOrcamento}</TableCell>
                        <TableCell>
                          {c.nomeRazao || <span className="text-muted-foreground">—</span>}
                          {c.cpfCnpj && <div className="text-xs text-muted-foreground">{c.cpfCnpj}</div>}
                        </TableCell>
                        <TableCell>{c.vendedorNome || <span className="text-muted-foreground">—</span>}</TableCell>
                        <TableCell>{dataBr(c.dtEmissao)}</TableCell>
                        <TableCell>{dataBr(c.dtValidade)}</TableCell>
                        <TableCell className="text-right font-medium">{BRL(c.totalLiquido ?? 0)}</TableCell>
                        <TableCell>
                          <Badge className={cfg.className}>
                            <span className="flex items-center gap-1"><Icon className="h-3 w-3" />{cfg.label}</span>
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            {podeCriar && (
                              <Button size="sm" variant="ghost"
                                onClick={() => navigate(`/operacional/condicional/${c.id}`)} title="Editar">
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" title="Ver detalhes"
                              onClick={() => navigate(`/operacional/condicional/${c.id}`)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            {podeCriar && (
                              <Button size="sm" variant="ghost" onClick={() => setDeletingId(c.id)} title="Excluir">
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <PaginacaoRodape {...pag.rodape({ total, totalPaginas })} />

      <DeleteConfirmDialog
        open={!!deletingId}
        onOpenChange={(open) => !open && setDeletingId(null)}
        onConfirm={() => { if (deletingId) { deleteCondicional(deletingId); setDeletingId(null); } }}
        title="Excluir Condicional"
        description="Tem certeza que deseja excluir esta venda condicional?"
      />
    </div>
  );
};

export default Condicional;
